import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TODAY = new Date().toISOString().split('T')[0];
const PAYMENT_METHODS = ['Cash','Company Card','Employee Advance'];
const EMPTY_EDIT = { vehicle_id:'', expense_date:TODAY, quantity:'', cost:'', fuel_station:'', odometer_reading:'', payment_method:'Cash' };

function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  return new Date(s + 'T00:00:00').toLocaleDateString('en-PK', { day:'2-digit', month:'short', year:'numeric' });
}

export default function FuelExpenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState(EMPTY_EDIT);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterVehicle, setFilterVehicle] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (filterVehicle) params.append('vehicle_id', filterVehicle);
    if (filterFrom) params.append('from', filterFrom);
    if (filterTo) params.append('to', filterTo);
    try { const r = await api.get('/api/fuel-expenses?' + params.toString()); setExpenses(r.data); }
    catch (err) { console.error('fuel-expenses:', err); setExpenses([]); }
    try { const r = await api.get('/api/vehicles'); setVehicles(r.data); }
    catch (err) { console.error('vehicles:', err); setVehicles([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterVehicle, filterFrom, filterTo]);

  const totalCost = expenses.reduce((s, e) => s + Number(e.cost||0), 0);
  const totalQty  = expenses.reduce((s, e) => s + Number(e.quantity||0), 0);
  const avgCostPerLiter = totalQty > 0 ? (totalCost / totalQty).toFixed(2) : 0;

  const getVehicle = (id) => vehicles.find(v => v.id === Number(id));

  const handleEdit = (ex) => {
    setForm({
      vehicle_id: ex.vehicle_id || '',
      expense_date: String(ex.expense_date || TODAY).slice(0, 10),
      quantity: ex.quantity || '',
      cost: ex.cost || '',
      fuel_station: ex.fuel_station || '',
      odometer_reading: ex.odometer_reading || '',
      payment_method: ex.payment_method || 'Cash',
    });
    setEditing(ex.id);
    setShowEdit(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        vehicle_id: Number(form.vehicle_id),
        quantity: form.quantity ? Number(form.quantity) : null,
        cost: Number(form.cost),
        odometer_reading: form.odometer_reading ? Number(form.odometer_reading) : null,
      };
      await api.put(`/api/fuel-expenses/${editing}`, payload);
      setShowEdit(false); setForm(EMPTY_EDIT); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fuel expense record?')) return;
    try { await api.delete(`/api/fuel-expenses/${id}`); load(); }
    catch { alert('Delete failed'); }
  };

  const exportCSV = () => {
    const headers = ['Date','Vehicle','Quantity (L)','Cost (Rs)','Station','Odometer','Payment'];
    const rows = expenses.map(e => [
      String(e.expense_date || '').slice(0, 10),
      getVehicle(e.vehicle_id)?.vehicle_number || e.vehicle_id,
      e.quantity, e.cost, e.fuel_station || '', e.odometer_reading || '', e.payment_method
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'fuel_expenses.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Fuel Expenses</h1>
          <p className="text-gray-500 text-sm">{expenses.length} records · auto-generated from trip completions</p>
        </div>
        <button onClick={exportCSV} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
          📥 Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <p className="text-sm text-gray-500">Total Cost</p>
          <p className="text-2xl font-bold text-red-600 mt-1">Rs {totalCost.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <p className="text-sm text-gray-500">Total Quantity</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{totalQty.toFixed(1)} L</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <p className="text-sm text-gray-500">Avg Cost / Liter</p>
          <p className="text-2xl font-bold text-green-600 mt-1">Rs {Number(avgCostPerLiter).toLocaleString()}</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 flex items-start gap-2">
        <span className="text-lg">⛽</span>
        <span>Fuel expenses are automatically recorded when a vehicle trip is completed. To add a fuel record, complete a trip from the <strong>Vehicle Trips</strong> page.</span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3">
        <select value={filterVehicle} onChange={e => setFilterVehicle(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Vehicles</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
        </select>
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button onClick={() => { setFilterVehicle(''); setFilterFrom(''); setFilterTo(''); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">Clear</button>
      </div>

      {/* Edit Modal (no Add — records come from trips) */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Edit Fuel Expense</h2>
              <button onClick={() => { setShowEdit(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
                <select value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Vehicle —</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number} ({v.vehicle_type})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={form.expense_date} onChange={e => setForm({...form, expense_date: e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (Liters)</label>
                <input type="number" step="0.1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost (Rs) *</label>
                <input type="number" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Station</label>
                <input type="text" value={form.fuel_station} onChange={e => setForm({...form, fuel_station: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Odometer Reading</label>
                <input type="number" value={form.odometer_reading} onChange={e => setForm({...form, odometer_reading: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">Update</button>
                <button type="button" onClick={() => { setShowEdit(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">⛽</div>
            <p>Loading expenses...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">⛽</div>
            <p className="font-medium">No fuel expenses yet</p>
            <p className="text-sm mt-1">Complete a vehicle trip with fuel cost to see records here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date','Vehicle','Qty (L)','Cost (Rs)','Station','Odometer','Payment','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {expenses.map(ex => (
                  <tr key={ex.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(ex.expense_date)}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{getVehicle(ex.vehicle_id)?.vehicle_number || ex.vehicle_id}</td>
                    <td className="px-4 py-3 text-gray-600">{ex.quantity ? Number(ex.quantity).toFixed(1) : '—'}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">Rs {Number(ex.cost).toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-600">{ex.fuel_station || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{ex.odometer_reading || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{ex.payment_method}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(ex)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                        <button onClick={() => handleDelete(ex.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
